import { Batch, ProductDocumentTypeEnum } from '@/eshop/gql/graphql'
import { useK2Setup } from '@/eshop/hooks/useK2Setup'
import { getBatches, getProductDocument } from '@/eshop/queries'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ProductListItemRow } from '../ProductList/ListItem'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { downloadBase64File, hydrateResult } from '@/eshop/utils/client'
import Loader from '../Loader'
import Pagination from '../Pagination'

interface Props {
    search: string
}

const BATCHES_PER_PAGE = 20

export default function EshopBatchSection({ search }: Props) {
    const k2 = useK2Setup()
    const { t } = useTranslation()
    const [rowsInProgress, setRowsInProgress] = useState<number[]>([])
    const [page, setPage] = useState(1)
    const [total, setTotal] = useState(1)

    const { mutate: getDocument } = useMutation({
        mutationKey: ['batches', k2.languageId],
        mutationFn: ({ goodsId, name }: { goodsId: number; name: string }) =>
            getProductDocument()({
                lang: k2.languageId,
                batchId: goodsId,
                typeOfDoc: ProductDocumentTypeEnum.Certificate,
            }).toPromise(),
        onMutate: ({ goodsId }) => {
            setRowsInProgress((state) => [...state, goodsId])
        },
        onSettled: (res, err, { goodsId, name }) => {
            setRowsInProgress((state) => state.filter((id) => id !== goodsId))
            res?.match({
                Ok: (r) => {
                    downloadBase64File(name, r.productDocument.resultBase64!)
                },
                Error: () => {},
            })
        },
    })

    const { data, isFetched, isFetching } = useQuery({
        queryKey: ['batches', k2.languageId, search],
        queryFn: () => getBatches()(search).toPromise(),
    })

    useEffect(() => {
        setPage(1)
        if (data && data?.match) {
            setTotal(
                data.match({
                    Ok: (v) =>
                        v.batches.records?.length
                            ? Math.ceil(
                                  v.batches.records.length / BATCHES_PER_PAGE
                              )
                            : 1,
                    Error: () => 1,
                })
            )
            return
        }
        setTotal(1)
    }, [data])

    const pagedData = useMemo((): Batch[] => {
        if (isFetching && !isFetched) {
            return []
        }

        return (
            hydrateResult(data)?.match?.({
                Ok: (v) =>
                    (v.batches.records || []).filter(
                        (_, i) =>
                            i >= (page - 1) * BATCHES_PER_PAGE &&
                            i < BATCHES_PER_PAGE * page
                    ) as Batch[],
                Error: () => [],
            }) || []
        )
    }, [data, isFetched, isFetching, page])

    return (
        <div className="my-10">
            <Loader isLoading={isFetching && !isFetched}>
                <>
                    {pagedData.map?.((p) => (
                        <ProductListItemRow
                            key={p?.id}
                            name={p?.articleName || ''}
                            rightAdornment={
                                <div>
                                    {rowsInProgress.includes(p?.id || -1) ? (
                                        <div className="flex justify-center mx-10">
                                            <span className="loading loading-spinner"></span>
                                        </div>
                                    ) : (
                                        <span
                                            className="link"
                                            onClick={() =>
                                                getDocument({
                                                    goodsId: p?.id || -1,
                                                    name: p?.articleName || '',
                                                })
                                            }
                                        >
                                            {t('eshop.documents.cetificate')}
                                        </span>
                                    )}
                                </div>
                            }
                        />
                    ))}
                    <Pagination
                        className="mt-24"
                        current={page}
                        total={total}
                        onPageChange={setPage}
                    />
                </>
            </Loader>
        </div>
    )
}
